import { ChangeDetectorRef, Component, ComponentRef, ElementRef, EventEmitter, inject, Injector, Input, Output, Renderer2, ViewChild, ViewContainerRef } from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { Subject, takeUntil } from 'rxjs'
import { DropdownContentComponent } from '../dropdown-content/dropdown-content.component'
import { ComponentWithDataSetter, DropdownOption } from './types/dropdown-option.type'
type yPositions = 'top' | 'bottom' | 'remain_at_bottom'

type xPositions = 'left' | 'right' | 'auto'

@Component({
  selector: 'app-dropdown',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './dropdown.component.html'
})
export class DropdownComponent {
  /** Above Nebular/CDK `.cdk-overlay-container` (10400) so portaled menus stay visible after SPA navigation. */
  private static readonly PORTAL_Z_INDEX = 12000

  @Input() hiddenOption?: string | number
  //pass an array of options to the dropdown
  private dropdownOptions?: DropdownOption[] = []
  @Input('dropdownOptions') set dropdownOptionsChanges(options: DropdownOption[]) {
    this.dropdownOptions = options
    if (this._dropdownRef) this._dropdownRef.instance.dropdownOptions = this.dropdownOptions
  }

  @Input() id: string = ''
  //pass a custom component to the dropdown
  @Input() componentWithDataSetter?: ComponentWithDataSetter<any>
  // Width for dropdown
  @Input() widthInPX: string = '187px'
  // Min Width for dropdown
  @Input() minWidthInPX: string = '70px'
  // Height for internal dropdown content
  @Input() set heightInPX(value: number) {
    this._heightInPX = value
    if (this._dropdownRef) this._dropdownRef.instance.heightInPX = value
  }
  get heightInPX(): number {
    return this._heightInPX
  }
  private _heightInPX: number = 120
  // Max Height for internal dropdown content
  @Input() maxHeightInPX: number = 340
  // Optional vertical gap between options
  @Input() optionGapInPX: number = 0
  // The x position of the dropdown list
  @Input() dropdownListXPosition: xPositions = 'auto'
  // The y position of the dropdown list
  @Input() dropdownListYPosition: yPositions = 'bottom'
  /**
   * When true, the panel is anchored to the opening pointer position (click/tap) instead of the
   * trigger host's bounding box. Uses the `MouseEvent` passed to `toggleDropdown`; falls back to
   * trigger-based placement if the event is missing or not a mouse event.
   */
  @Input() positionAtClick = false
  /**
   * Pixels subtracted from `top` when the panel opens below the trigger.
   * Not applied when the panel opens above.
   */
  @Input() topThreshold = 0
  // The flag to handle the style of the dropdowns for a field
  @Input() isField: boolean = false
  // The flag to handle the style of the dropdowns for a field
  @Input() isSingleSelect: boolean = true
  // When false, the panel stays open after an option click (closes on outside click, Escape, or trigger click)
  @Input() closeOnSelect: boolean = true
  // The flag to disable the dropdown
  @Input() disableDropdown: boolean = false
  // The selector to consider it inside the dropdown
  @Input() exceptionSelector: string = ''
  @Input() set optionNoData(value: string) {
    this._optionNoData = value
    // Update dropdown-content instance if it exists
    if (this._dropdownRef) {
      this._dropdownRef.instance.optionNoData = value
    }
  }
  get optionNoData(): string {
    return this._optionNoData
  }
  private _optionNoData: string = ''

  // Flag to force hide dropdown content even for field types (for city fields with no options)
  @Input() set forceHide(value: boolean) {
    this._updateDropdownProperty('forceHide', value, '_forceHide')
  }

  // Flag to indicate if user has searched for something
  @Input() set hasSearchQuery(value: boolean) {
    this._updateDropdownProperty('hasSearchQuery', value, '_hasSearchQuery')
  }
  @Output() dropdownClosed: EventEmitter<void> = new EventEmitter<void>()
  @Output() dropdownOpened: EventEmitter<void> = new EventEmitter<void>()
  @Output() optionClicked: EventEmitter<DropdownOption> = new EventEmitter<DropdownOption>()
  @ViewChild('dropdown') dropdown!: ElementRef<HTMLElement>

  public isOpen: boolean = false

  selectedOptionIcon: string = `<svg
  width="16"
  height="16"
  viewBox="0 0 16 16"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
  >
  <path
  d="M8 0C6.41775 0 4.87103 0.469192 3.55544 1.34824C2.23985 2.22729 1.21447 3.47672 0.608967 4.93853C0.00346627 6.40034 -0.15496 8.00887 0.153721 9.56072C0.462403 11.1126 1.22433 12.538 2.34315 13.6569C3.46197 14.7757 4.88743 15.5376 6.43928 15.8463C7.99113 16.155 9.59966 15.9965 11.0615 15.391C12.5233 14.7855 13.7727 13.7602 14.6518 12.4446C15.5308 11.129 16 9.58225 16 8C15.9977 5.87898 15.1541 3.8455 13.6543 2.34572C12.1545 0.845932 10.121 0.00232928 8 0ZM10.9656 6.9656L7.7656 10.1656C7.61558 10.3156 7.41213 10.3998 7.2 10.3998C6.98787 10.3998 6.78442 10.3156 6.6344 10.1656L5.0344 8.5656C4.88868 8.41472 4.80804 8.21263 4.80986 8.00288C4.81169 7.79312 4.89582 7.59247 5.04415 7.44414C5.19247 7.29582 5.39312 7.21168 5.60288 7.20986C5.81264 7.20804 6.01472 7.28867 6.1656 7.4344L7.2 8.4688L9.8344 5.8344C9.98528 5.68867 10.1874 5.60804 10.3971 5.60986C10.6069 5.61168 10.8075 5.69582 10.9559 5.84414C11.1042 5.99247 11.1883 6.19312 11.1901 6.40288C11.192 6.61263 11.1113 6.81472 10.9656 6.9656Z"
  fill="#1F6FE0"
  />
  </svg>`
  private _dropdownRef?: ComponentRef<DropdownContentComponent>
  private _destroy$: Subject<void> = new Subject<void>()
  private _isClicked: boolean = false
  private _renderer = inject(Renderer2)
  private _cdr = inject(ChangeDetectorRef)
  private _hasSearchQuery: boolean = false
  private _forceHide: boolean = false
  private _bodyScrollLocked = false
  private _scrollLockedAncestors: HTMLElement[] = []
  /** Unique id for this instance (from @Input id or generated). Used for nested dropdowns. */
  private _instanceId?: string

  constructor(private injector: Injector, private viewContainerRef: ViewContainerRef) {}

  /**
   * @description Helper method to update dropdown properties and sync with dropdown instance
   * @param instanceProperty The property name on the dropdown instance
   * @param value The new value to set
   * @param privateProperty The private property name on this component
   */
  private _updateDropdownProperty(instanceProperty: string, value: boolean, privateProperty: string): void {
    ;(this as any)[privateProperty] = value
    if (this._dropdownRef) (this._dropdownRef.instance as any)[instanceProperty] = value
  }

  ngOnDestroy(): void {
    this._removeDropdown()
    this._destroy$.next()
    this._destroy$.complete()
  }

  /**
   * @description Check if the current document direction is RTL
   * @returns boolean
   */
  private _isRTL(): boolean {
    return document.documentElement.dir === 'rtl' || document.body.dir === 'rtl' || getComputedStyle(document.documentElement).direction === 'rtl'
  }

  /** `top` after applying {@link topThreshold} (`computedTop - topThreshold`). */
  private _applyTopThresholdToTopPx(topPx: number): number {
    const threshold = Number(this.topThreshold) || 0
    return topPx - threshold
  }

  private _applyPortalLayerStyles(domElem: HTMLElement): void {
    domElem.style.position = 'fixed'
    domElem.style.zIndex = `${DropdownComponent.PORTAL_Z_INDEX}`
  }

  /**
   * @description Collect the scrollable ancestors
   * @returns HTMLElement[]
   */
  private _collectScrollableAncestors(): HTMLElement[] {
    const ancestors: HTMLElement[] = []
    let node: HTMLElement | null = this.dropdown?.nativeElement?.parentElement ?? null

    while (node && node !== document.body) {
      const { overflowY } = getComputedStyle(node)
      const isScrollableOverflow = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay'
      if (isScrollableOverflow && node.scrollHeight > node.clientHeight) ancestors.push(node)
      node = node.parentElement
    }

    return ancestors
  }

  /**
   * @description Lock the outer scroll while the dropdown is open
   * @returns void
   */
  private _lockOuterScrollWhileOpen(): void {
    // Only release locks this dropdown added, so an open modal keeps its own body lock.
    if (!document.body.classList.contains('tw-overflow-hidden')) {
      this._renderer.addClass(document.body, 'tw-overflow-hidden')
      this._bodyScrollLocked = true
    }
    this._scrollLockedAncestors = this._collectScrollableAncestors().filter((ancestor) => !ancestor.classList.contains('tw-overflow-hidden'))

    for (const ancestor of this._scrollLockedAncestors) {
      this._renderer.addClass(ancestor, 'tw-overflow-hidden')
    }
  }

  /**
   * @description Unlock the outer scroll while the dropdown is closed
   * @returns void
   */
  private _unlockOuterScrollWhileOpen(): void {
    if (this._bodyScrollLocked) {
      this._renderer.removeClass(document.body, 'tw-overflow-hidden')
      this._bodyScrollLocked = false
    }

    for (const ancestor of this._scrollLockedAncestors) {
      this._renderer.removeClass(ancestor, 'tw-overflow-hidden')
    }

    this._scrollLockedAncestors = []
  }

  /**
   * @description Set the open state of the dropdown
   * @param open boolean
   * @returns void
   */
  private _setOpenState(open: boolean): void {
    this.isOpen = open
    this._cdr.markForCheck()
  }

  /**
   * @description Toggle dropdown list
   * @param event accepts Event
   * @returns void
   */
  public toggleDropdown(event?: Event): void {
    if (this._isClicked) return
    this._isClicked = true
    if (this._dropdownRef) this._removeDropdown()
    else this._createDropdownList(event)
    setTimeout(() => (this._isClicked = false))
  }

  /**
   * Open the list only when closed. Use for focus-driven opens — toggle() would close a
   * still-mounted panel or no-op while _isClicked is true (Safari is stricter here).
   */
  public openDropdown(event?: Event): void {
    if (this.disableDropdown || this._dropdownRef) return
    this._createDropdownList(event ?? new Event('focus'))
  }

  /** Close the overlay if open (e.g. when clearing the field). */
  public closeDropdown(): void {
    if (this._dropdownRef) this._removeDropdown()
  }

  /** Re-anchor an open panel after the trigger height changes (multi-select chips). */
  public reposition(): void {
    if (!this._dropdownRef) return
    setTimeout(() => {
      if (this._dropdownRef) this._placeDropdown(new Event('scroll'))
    })
  }

  /**
   * @description Create dropdown list
   * @param event accepts Event
   * @returns void
   */
  private _createDropdownList(event?: Event): void {
    if (this.disableDropdown) return
    if (this._dropdownRef) return
    // Create the component using ViewContainerRef
    this._dropdownRef = this.viewContainerRef.createComponent(DropdownContentComponent, {
      injector: this.injector
    })
    if (this.isField) this.widthInPX = this.dropdown.nativeElement.getBoundingClientRect().width + 'px'

    this._initializeCreatedDropdownData()

    const domElem = (this._dropdownRef.hostView as any).rootNodes[0] as HTMLElement

    // Unique id for this instance so nested dropdowns target the correct overlay/content
    this._instanceId = this.id || `dropdown-${crypto.randomUUID()}`
    domElem.setAttribute('data-dropdown-id', this._instanceId)

    // Create overlay (tagged so we only remove this instance's overlay when closing)
    const overlayElement = document.createElement('div')
    overlayElement.classList.add('dropdown-list-overlay', 'tw-w-screen', 'tw-h-screen', 'tw-opacity-0', 'tw-fixed', 'tw-top-0', 'tw-left-0')
    overlayElement.style.zIndex = `${DropdownComponent.PORTAL_Z_INDEX}`
    overlayElement.setAttribute('data-dropdown-id', this._instanceId)
    document.body.appendChild(overlayElement)

    // Append the dropdown to the body
    domElem.style.visibility = 'hidden'
    this._applyPortalLayerStyles(domElem)
    document.body.appendChild(domElem)

    // Lock outer scroll so portaled panels stay aligned with their trigger.
    this._lockOuterScrollWhileOpen()
    this._setOpenState(true)

    setTimeout(
      () => {
        this._placeDropdown(event)
        // Make visible after positioning
        domElem.style.visibility = 'visible'
        this.dropdownOpened.emit()
        // Listen for click outside to close the dropdown
        document.addEventListener('mousedown', this._onDocumentClick)
      },
      this.isField ? 100 : 0
    )
  }

  /**
   * @description Initialize the created dropdown inputs & outputs
   * @returns void
   */
  private _initializeCreatedDropdownData(): void {
    if (!this._dropdownRef) return
    this._dropdownRef.instance.componentWithDataSetter = this.componentWithDataSetter
    this._dropdownRef.instance.dropdownOptions = this.dropdownOptions
    this._dropdownRef.instance.widthInPX = this.widthInPX
    this._dropdownRef.instance.minWidthInPX = this.minWidthInPX
    this._dropdownRef.instance.heightInPX = this.heightInPX
    this._dropdownRef.instance.maxHeightInPX = this.maxHeightInPX
    this._dropdownRef.instance.optionGapInPX = this.optionGapInPX
    this._dropdownRef.instance.hiddenOption = this.dropdownOptions?.find((option) => option.key === this.hiddenOption)
    this._dropdownRef.instance.isField = this.isField
    this._dropdownRef.instance.isSingleSelect = this.isSingleSelect
    this._dropdownRef.instance.hasSearchQuery = this._hasSearchQuery
    this._dropdownRef.instance.forceHide = this._forceHide
    this._dropdownRef.instance.optionNoData = this.optionNoData
    this._dropdownRef.instance.optionClicked.pipe(takeUntil(this._destroy$)).subscribe({
      next: (option) => {
        if (this.closeOnSelect) this._removeDropdown()
        this.optionClicked.emit(option)
      }
    })
    this._dropdownRef.instance.dropdownClosed.pipe(takeUntil(this._destroy$)).subscribe({
      next: () => this._removeDropdown()
    })
  }

  /**
   * Positions the panel at the click coordinates (viewport X, document Y to match existing top math).
   * Clamps to the viewport; flips above the pointer when `dropdownListYPosition` is `bottom` and there is not enough space below.
   */
  private _placeDropdownAtPointer(event: MouseEvent, domElem: HTMLElement): void {
    this._applyPortalLayerStyles(domElem)
    const pad = 4
    const isRTL = this._isRTL()
    const vw = window.innerWidth
    const cx = event.clientX
    const cy = event.clientY

    domElem.style.right = 'unset'
    domElem.style.bottom = 'unset'
    domElem.style.transform = ''

    const pr = domElem.getBoundingClientRect()
    const w = pr.width
    const h = pr.height

    let topViewport: number
    if (this.dropdownListYPosition === 'top') {
      topViewport = cy - h - pad
    } else if (this.dropdownListYPosition === 'remain_at_bottom') {
      topViewport = cy + pad
    } else {
      const belowBottom = cy + pad + h
      if (belowBottom > window.innerHeight && cy - h - pad >= pad) {
        topViewport = cy - h - pad
      } else {
        topViewport = cy + pad
      }
    }

    const minTop = pad
    const maxTop = window.innerHeight - h - pad
    const clampedTop = Math.max(minTop, Math.min(topViewport, maxTop))
    domElem.style.top = `${Math.max(minTop, this._applyTopThresholdToTopPx(clampedTop))}px`

    if (this.isField) {
      domElem.style.right = 'unset'
      const leftPx = Math.max(pad, Math.min(cx, vw - w - pad))
      domElem.style.left = `${leftPx}px`
      return
    }

    let leftPx: number
    const triggerViewportLeft = this.dropdown.nativeElement.getBoundingClientRect().left
    if (this.dropdownListXPosition === 'left') {
      leftPx = isRTL ? cx : cx - w
    } else if (this.dropdownListXPosition === 'right') {
      // Match trigger-based placement: panel starts at the trigger's left edge, not at clientX.
      leftPx = triggerViewportLeft
    } else {
      if (isRTL) {
        leftPx = cx - w / 2
      } else {
        leftPx = cx
        if (leftPx + w > vw - pad) {
          leftPx = cx - w
        }
      }
    }

    domElem.style.right = 'unset'
    leftPx = Math.max(pad, Math.min(leftPx, vw - w - pad))
    domElem.style.left = `${leftPx}px`
  }

  /**
   * @description Place dropdown component functionalities
   * @param event accepts Event
   * @returns void
   */
  private _placeDropdown(event?: Event) {
    // If this is a scroll event, use the stored dropdown element
    const element = event.type === 'scroll' ? this.dropdown.nativeElement : this.dropdown.nativeElement || (event.target as HTMLElement)

    // Target this instance's content (required for dropdown-inside-dropdown)
    const domElem = (
      this._instanceId ? document.querySelector(`app-dropdown-content[data-dropdown-id="${this._instanceId}"]`) : document.querySelector('app-dropdown-content')
    ) as HTMLElement
    if (!domElem) return

    if (this.positionAtClick && event instanceof MouseEvent) {
      this._placeDropdownAtPointer(event, domElem)
      domElem.classList.add('tw-fade-in-animation')
      return
    }

    this._applyPortalLayerStyles(domElem)

    const isRTL = this._isRTL()
    const edgePad = 4

    // Get the trigger element's position - this is the element that should trigger the dropdown
    const triggerElement = this.dropdown.nativeElement
    const triggerRect = triggerElement.getBoundingClientRect()
    const panelHeight = domElem.getBoundingClientRect().height

    // Viewport coordinates (panel is `position: fixed` on `document.body`)
    const top = triggerRect.top - panelHeight
    const down = triggerRect.bottom
    const aboveBottom = window.innerHeight - triggerRect.top + 6

    let isInMiddleOfScreen: boolean = false

    // Handle Y positioning - always position below the trigger element
    switch (this.dropdownListYPosition) {
      case 'top': {
        if (top < 0) {
          if (down + panelHeight > window.innerHeight) {
            isInMiddleOfScreen = true
            domElem.style.top = '50%'
            domElem.style.transform = 'translateY(-50%)'
          } else {
            domElem.style.top = `${this._applyTopThresholdToTopPx(down)}px`
          }
        } else {
          domElem.style.bottom = `${aboveBottom}px`
          domElem.style.top = 'unset'
        }
        break
      }
      case 'bottom': {
        if (down + panelHeight > window.innerHeight) {
          if (top < 0) {
            isInMiddleOfScreen = true
            domElem.style.top = '50%'
            domElem.style.transform = 'translateY(-50%)'
          } else {
            domElem.style.bottom = `${aboveBottom}px`
            domElem.style.top = 'unset'
          }
        } else {
          domElem.style.top = `${this._applyTopThresholdToTopPx(down)}px`
        }
        break
      }
      case 'remain_at_bottom': {
        domElem.style.top = `${this._applyTopThresholdToTopPx(down)}px`
        break
      }
    }

    // Handle X positioning - align with the trigger element

    switch (this.dropdownListXPosition) {
      case 'left': {
        if (this.isField) {
          // For fields, align with the left edge of the trigger element
          if (isRTL) {
            domElem.style.right = `${window.innerWidth - triggerRect.right}px`
            domElem.style.left = 'unset'
          } else {
            domElem.style.left = `${triggerRect.left}px`
            domElem.style.right = 'unset'
          }
          break
        }

        // For non-field dropdowns, position to the left of the trigger
        if (isRTL) {
          // Align the panel's right edge with the trigger's right edge (mirror of LTR).
          domElem.style.right = `${window.innerWidth - triggerRect.right}px`
          domElem.style.left = 'unset'
          if (domElem.getBoundingClientRect().left < edgePad) {
            domElem.style.left = `${triggerRect.left}px`
            domElem.style.right = 'unset'
          }
        } else {
          const w = domElem.getBoundingClientRect().width
          domElem.style.left = `${triggerRect.right - w}px`
          domElem.style.right = 'unset'
          if (domElem.getBoundingClientRect().left < edgePad) {
            domElem.style.left = `${triggerRect.left}px`
          }
        }
        break
      }
      case 'right': {
        if (this.isField) {
          // Same as non-field: anchor the panel to the trigger's left edge (not the right).
          domElem.style.left = `${triggerRect.left}px`
          domElem.style.right = 'unset'
          break
        }

        // Panel's left edge at the trigger's left edge (same idea as `auto`); clamp if it overflows the viewport.
        {
          const w = domElem.getBoundingClientRect().width
          domElem.style.left = `${triggerRect.left}px`
          domElem.style.right = 'unset'
          let r = domElem.getBoundingClientRect()
          if (r.right > window.innerWidth - edgePad) {
            domElem.style.left = `${triggerRect.right - w}px`
            r = domElem.getBoundingClientRect()
            if (r.left < edgePad) {
              domElem.style.left = `${triggerRect.left}px`
            }
          }
        }
        break
      }
      case 'auto': {
        // Auto positioning - align the dropdown with the trigger element
        // For user profile dropdowns, position directly below the trigger
        if (isRTL) {
          // In RTL, center the dropdown under the trigger button
          const triggerCenter = triggerRect.left + triggerRect.width / 2
          const dropdownWidth = domElem.getBoundingClientRect().width
          const leftViewport = triggerCenter - dropdownWidth / 2

          // Ensure the dropdown stays within the viewport
          if (leftViewport < edgePad) {
            domElem.style.left = `${triggerRect.left}px`
            domElem.style.right = 'unset'
          } else if (leftViewport + dropdownWidth > window.innerWidth - edgePad) {
            domElem.style.left = `${triggerRect.right - dropdownWidth}px`
            domElem.style.right = 'unset'
          } else {
            domElem.style.left = `${leftViewport}px`
            domElem.style.right = 'unset'
          }
        } else {
          const w = domElem.getBoundingClientRect().width
          domElem.style.left = `${triggerRect.left}px`
          domElem.style.right = 'unset'

          let r = domElem.getBoundingClientRect()
          if (r.right > window.innerWidth - edgePad) {
            domElem.style.left = `${triggerRect.right - w}px`
            r = domElem.getBoundingClientRect()
            if (r.left < edgePad) {
              domElem.style.left = `${triggerRect.left}px`
            }
          }
        }
        break
      }
    }

    // Move the dropdown content if it's in the middle of the screen
    if (isInMiddleOfScreen) {
      const vl = domElem.getBoundingClientRect().left
      switch (this.dropdownListXPosition) {
        case 'left': {
          const nextViewportLeft = isRTL ? vl + triggerRect.width : vl - triggerRect.width
          domElem.style.left = `${nextViewportLeft}px`
          domElem.style.right = 'unset'
          break
        }
        case 'right': {
          // Same horizontal anchor as `auto` (trigger left); no X nudge when vertically centered.
          break
        }
      }
    }

    domElem.classList.add('tw-fade-in-animation')
  }

  /**
   * @description Close the dropdown (public API for custom content to close after apply/reset).
   * @returns void
   */
  public close(): void {
    this._removeDropdown()
  }

  /**
   * @description Remove the dropdown list
   * @returns void
   */
  private _removeDropdown(): void {
    if (this._dropdownRef) {
      this._dropdownRef.destroy()
      this._dropdownRef = undefined
      // Remove only this instance's overlay (supports nested dropdowns)
      if (this._instanceId) {
        document.querySelector(`.dropdown-list-overlay[data-dropdown-id="${this._instanceId}"]`)?.remove()
      } else {
        document.querySelector('.dropdown-list-overlay')?.remove()
      }
      this._instanceId = undefined
      document.removeEventListener('mousedown', this._onDocumentClick)
      this._unlockOuterScrollWhileOpen()
      this._setOpenState(false)
      this.dropdownClosed.emit()
    }
  }

  /**
   * @description Handle click outside the dropdown
   * @param event accepts MouseEvent
   * @returns void
   */
  private _onDocumentClick = (event: MouseEvent): void => {
    if (!this._dropdownRef) return
    const el = event.target as HTMLElement | null
    if (!el) return

    if (this.dropdown.nativeElement.contains(el)) return

    // Nested dropdowns append their own overlay + content to `document.body`. A mousedown on
    // another instance's layer must not close this one (e.g. dismissing only the inner picker).
    const foreignOverlay = el.closest('.dropdown-list-overlay')
    if (foreignOverlay && foreignOverlay.getAttribute('data-dropdown-id') !== this._instanceId) return

    const foreignContent = el.closest('app-dropdown-content')
    if (foreignContent && foreignContent.getAttribute('data-dropdown-id') !== this._instanceId) return

    if (el.closest('.dropdown-list-container')) return

    const ex = (this.exceptionSelector || '').trim()
    if (ex && el.closest(ex)) return

    this._removeDropdown()
  }
}
